import Button, { ButtonVariants } from '@/components/atoms/Button/Button';
import PaginationRanges from '@/components/atoms/Pagination/Pagination';
import Badge, { BadgeVariants } from '@/components/molecules/Badge';
import { DropdownMenuItemVariants } from '@/components/molecules/DropdownMenuItem';
import { EVSavingsToggle } from '@/components/molecules/EVSavingsToggle/EVSavingsToggle';
import FormCheckbox from '@/components/molecules/FormCheckbox';
import FormSelect from '@/components/molecules/FormSelect';
import { LoadingProductListingLink } from '@/components/organisms/LoadingCards/LoadingCards';
import { ProductListingLink } from '@/components/organisms/ProductListingLink/ProductListingLink';
import { ProductListingLinkWithoutIncentives } from '@/components/organisms/ProductListingLinkWithoutIncentives/ProductListingLinkWithoutIncentives';
import { initialSavedVehicles } from '@/context/SavedVehicles';
import { useVehicleFilterSettingsContext } from '@/context/VehicleFilterSettings';
import { useAuth } from '@/hooks/useAuth';
import useWindowSize from '@/hooks/useWindowSize';
import { SORT_BY_OPTIONS } from '@/lib/constants';
import { VehicleListingType } from '@/lib/schema/inventory/types';
import { getUserAuthHeader } from '@/lib/schema/user/client-fetch';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/24/solid';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import React, { useCallback } from 'react';

export const PAGE_SIZE = 12;

interface Filters {
  sort: string;
  make: string[];
  model: string[];
}

interface FilterOverlayProps {
  loading: boolean;
  dealerName: string;
  totalListings: number;
  filters: Filters;
  setFilters: (vals: Filters) => void;
  availableMakes: string[];
  availableModels: Record<string, string[]>;
}

export const FilterOverlay: React.FC<FilterOverlayProps> = (props) => {
  const { isMobile } = useWindowSize();
  const {
    settings: { evSavings, displayFuelSavings, displayIncentives },
    handleSetDisplayFuelSavings,
    handleSetDisplayIncentives,
  } = useVehicleFilterSettingsContext();

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <Button
          aria-label="filter results"
          variant={ButtonVariants.Secondary}
          size={isMobile ? 'small' : 'default'}
          icon={{
            icon: (
              <AdjustmentsHorizontalIcon className="size-[20px] stroke-brandMidnight" />
            ),
          }}
        >
          Filter results
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-10 bg-[#08081266]" />
        <Dialog.Content className="fixed bottom-0 left-0 right-0 top-0 z-20 overflow-y-auto bg-white pb-safe-bottom m:left-auto m:w-[620px]">
          <div className="flex flex-col gap-xl border-b border-neutral-300 px-xl pb-l pt-xl m:h-[60px] m:justify-center m:py-m m:pr-[60px]">
            <div className="flex justify-between gap-xl">
              <div className="text-h4Regular text-neutral-900">Filters</div>
              <Dialog.Close>
                <XMarkIcon className="size-xl fill-brandMidnight" />
              </Dialog.Close>
            </div>
            {(props.filters.make.length !== 0 ||
              props.filters.model.length !== 0) && (
              <div className="flex flex-wrap gap-s m:hidden">
                {props.filters.make.map((make) => (
                  <Badge
                    key={make}
                    label={make}
                    variant={BadgeVariants.Blue}
                    onClick={() => {
                      props.setFilters({
                        ...props.filters,
                        make: props.filters.make.filter((m) => m !== make),
                      });
                    }}
                    iconPosition="right"
                    icon={
                      <XMarkIcon width={20} height={20} strokeWidth={1.5} />
                    }
                  />
                ))}
                {props.filters.model.map((model) => (
                  <Badge
                    key={model}
                    label={model}
                    variant={BadgeVariants.Blue}
                    onClick={() => {
                      props.setFilters({
                        ...props.filters,
                        model: props.filters.model.filter((m) => m !== model),
                      });
                    }}
                    iconPosition="right"
                    icon={
                      <XMarkIcon width={20} height={20} strokeWidth={1.5} />
                    }
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-xl px-xl pb-3xl pt-l">
            <div className="text-neutral-900 m:hidden">
              <h2 className="inline text-h4SemiBold">
                Search {props.dealerName}
              </h2>{' '}
              <span className="text-h4Regular">
                {props.loading ? '...' : props.totalListings} EVs for sale
              </span>
            </div>
            <div className="border-b border-neutral-200 pb-xl">
              <FormSelect
                label="Sort by"
                variant={DropdownMenuItemVariants.SingleSelect}
                options={SORT_BY_OPTIONS}
                value={[props.filters.sort]}
                onChange={([value]) => {
                  props.setFilters({ ...props.filters, sort: value });
                }}
              />
            </div>
            <div className="flex flex-col gap-l border-b border-neutral-200 pb-xl">
              <FormSelect
                label="Make"
                placeholder="Select Make(s)"
                variant={DropdownMenuItemVariants.MultiSelect}
                options={props.availableMakes.map((make) => ({
                  text: make,
                  value: make,
                }))}
                value={props.filters.make}
                onChange={(selectedMakes) => {
                  // clear unselected models as well
                  const newAvailableModels = Object.entries(
                    props.availableModels
                  )
                    .filter(([make]) => selectedMakes.includes(make))
                    .map(([, models]) => models)
                    .flat();

                  const newSelectedModels = props.filters.model.filter(
                    (model) => newAvailableModels.includes(model)
                  );

                  props.setFilters({
                    ...props.filters,
                    make: selectedMakes,
                    model: newSelectedModels,
                  });
                }}
              />
              <FormSelect
                label="Model"
                placeholder="Select Model(s)"
                disabled={props.filters.make.length === 0}
                variant={DropdownMenuItemVariants.MultiSelect}
                options={Object.values(props.availableModels)
                  .flat()
                  .map((model) => ({
                    text: model,
                    value: model,
                  }))}
                value={props.filters.model}
                onChange={(models) => {
                  props.setFilters({ ...props.filters, model: models });
                }}
              />
            </div>
            <div className="flex flex-col gap-l">
              <div className="flex justify-between">
                <div className="text-h4Regular text-brandMidnight">
                  EV Savings
                </div>
                <EVSavingsToggle />
              </div>
              {evSavings && (
                <div className="flex flex-col gap-s">
                  {process.env.NEXT_PUBLIC_ENABLE_INCENTIVES_REBATES ===
                    'true' && (
                    <FormCheckbox
                      checkboxLabel="Incentives & rebates"
                      className="rounded-small bg-neutral-100 px-l py-m hover:bg-neutral-200"
                      checkBoxStyle="size-[20px] rounded-xsmall"
                      checked={displayIncentives}
                      onChange={(val) => handleSetDisplayIncentives(val)}
                    />
                  )}
                  <FormCheckbox
                    checkboxLabel="Gas savings (estimate)"
                    className="rounded-small bg-neutral-100 px-l py-m hover:bg-neutral-200"
                    checkBoxStyle="size-[20px] rounded-xsmall"
                    checked={displayFuelSavings}
                    onChange={(val) => handleSetDisplayFuelSavings(val)}
                  />
                  <div className="text-microLight text-neutral-800">
                    Switch to include prices with or without estimated
                    incentives, rebates, and gas savings.
                  </div>
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export interface DealerInventoryProps extends FilterOverlayProps {
  loading: boolean;
  listings: VehicleListingType[];
  pages: {
    current: number;
    total: number;
    handleNext: () => void;
    handlePrevious: () => void;
    onChange: (page: number) => void;
  };
}

const DealerInventory: React.FC<DealerInventoryProps> = (props) => {
  const { isMobile } = useWindowSize();
  const { user } = useAuth();

  const fetchSavedVehicles = useCallback(async () => {
    if (!user) {
      return initialSavedVehicles;
    }
    const authHeader = await getUserAuthHeader();
    if (!authHeader) {
      throw new Error("Couldn't fetch saved vehicles");
    }
    const response = await fetch('/api/user/user_store', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
    });
    if (response.ok) {
      const result = await response.json();
      return result;
    }
    throw new Error("Couldn't fetch saved vehicles");
  }, [user]);

  const { data: savedVehicles } = useQuery({
    queryKey: ['savedVehicles', user],
    queryFn: fetchSavedVehicles,
    throwOnError: false,
  });

  return (
    <div className="flex flex-col gap-l pb-3xl pt-xl">
      <div className="hidden items-center justify-between m:flex">
        <div className="flex gap-s">
          <h2 className="text-h4SemiBold text-neutral-900">
            {props.dealerName}
          </h2>
          <div className="text-h4Regular text-neutral-900">
            {props.loading ? '...' : props.totalListings} EVs for sale
          </div>
        </div>
        <div>
          <FilterOverlay {...props} />
        </div>
      </div>
      <div className="flex flex-col gap-l m:grid m:grid-cols-2 ml:grid-cols-3 l:grid-cols-4">
        {props.loading
          ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
              <LoadingProductListingLink
                key={`loading-card-${index}`}
                isColumnLayout={true}
              />
            ))
          : props.listings.map((vehicle) => {
              return process.env.NEXT_PUBLIC_ENABLE_INCENTIVES_REBATES ===
                'true' ? (
                <ProductListingLink
                  key={vehicle.listingId}
                  product={vehicle}
                  isListedPrice={true}
                  isMobile={isMobile}
                  showNewCheckAvailabilityButton={true}
                  contactedDealer={
                    !!savedVehicles?.listing?.[vehicle.listingId]
                      ?.contact_dealership
                  }
                  isSaved={!!savedVehicles?.listing?.[vehicle.listingId]}
                  isColumnLayout={true}
                  showFooter={false}
                />
              ) : (
                <ProductListingLinkWithoutIncentives
                  key={vehicle.listingId}
                  product={vehicle}
                  isListedPrice={true}
                  isMobile={isMobile}
                  showNewCheckAvailabilityButton={true}
                  contactedDealer={
                    !!savedVehicles?.listing?.[vehicle.listingId]
                      ?.contact_dealership
                  }
                  isSaved={!!savedVehicles?.listing?.[vehicle.listingId]}
                  isColumnLayout={true}
                  showFooter={false}
                />
              );
            })}
      </div>
      <div className="py-l m:pt-xl">
        <PaginationRanges
          count={props.pages.total}
          onChange={(_, index) => props.pages.onChange(index)}
          page={props.pages.current}
          handlePreviousPage={props.pages.handlePrevious}
          handleNextPage={props.pages.handleNext}
        />
      </div>
    </div>
  );
};

export default DealerInventory;
